import { useEffect, useRef, useState } from "react";
import { smbClientIntakeRetrievedLinkSchema } from "@shared/smb-client-intake-admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Intentionally not a query/mutation: credentials live only in this open dialog.
export function ClientIntakeViewLinkDialog({ id, copyOnLoad = false, onClose }: {
  id: string; copyOnLoad?: boolean; onClose: () => void;
}) {
  const alive = useRef(true);
  const [result, setResult] = useState<{ url: string; status: string } | null>(null);
  const [error, setError] = useState(""), [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(`/api/admin/smb/client-intake/links/${encodeURIComponent(id)}/url`, {
          credentials: "include", cache: "no-store", signal: controller.signal,
        });
        if (!response.ok) {
          // Never reflect response bodies or parser errors containing credentials.
          throw new Error(response.status === 409 ? "historical" : "unavailable");
        }
        const data = smbClientIntakeRetrievedLinkSchema.parse(await response.json());
        if (!alive.current || controller.signal.aborted) return;
        setResult(data);
        if (copyOnLoad) {
          try {
            await navigator.clipboard.writeText(data.url);
            if (alive.current && !controller.signal.aborted) setCopied(true);
          } catch {
            if (alive.current && !controller.signal.aborted) setError("Clipboard unavailable. Select and copy the link below.");
          }
        }
      } catch (err) {
        if (alive.current && !controller.signal.aborted) setError(err instanceof Error && err.message === "historical"
          ? "Restore this historical link using an original copy from its entry. If no original copy exists, Regenerate is a separate explicit replacement."
          : "The link could not be loaded. Check your access and application encryption configuration, then close and reopen to retry.");
      } finally { if (alive.current && !controller.signal.aborted) setLoading(false); }
    })();
    return () => { alive.current = false; controller.abort(); };
  }, [id, copyOnLoad]);
  const close = () => { alive.current = false; setResult(null); onClose(); };
  return <Dialog open onOpenChange={open => { if (!open) close(); }}>
    <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto">
      <DialogTitle>View personal client link</DialogTitle>
      <DialogDescription>Viewing does not change the link or the client's session. You can return here whenever you need it.</DialogDescription>
      {loading && <p role="status">Loading link...</p>}
      {result && <div className="min-w-0 space-y-3">
        <p className="text-sm">Status: {result.status}{result.status !== "active" ? " — this link cannot open the form. Viewing it does not reactivate it." : ""}</p>
        <Input aria-label="Personal client link" autoComplete="off" readOnly value={result.url}
          onFocus={event => event.currentTarget.select()} className="min-w-0" />
        <Button onClick={async () => {
          try { await navigator.clipboard.writeText(result.url); if (alive.current) { setCopied(true); setError(""); } }
          catch { if (alive.current) setError("Clipboard unavailable. Select and copy the link above."); }
        }}>{copied ? "Copied" : "Copy link"}</Button>
      </div>}
      {error && <p role="alert" className="break-words text-sm text-destructive">{error}</p>}
      <Button variant="outline" onClick={close}>Close</Button>
    </DialogContent>
  </Dialog>;
}
